import { Fragment, useEffect, useRef, useState } from 'react';

import { cn } from '@poseidon/components/cn';

import type { SequenceStep, SequenceStepType } from '~/components/Sequence/sequenceSteps';
import type { WaitUnit } from '~/components/Sequence/WaitDurationInput';
import { SequenceGraphInsert } from '~/components/Sequence/SequenceGraphInsert';
import { SequenceGraphNode } from '~/components/Sequence/SequenceGraphNode';
import { insertDisabledReason, sequenceStepIssue, stepIsEditable } from '~/components/Sequence/sequenceSteps';

interface Props {
  /** The branch's ordered steps (a spawn's `arm`, or one switch branch's `arm`). */
  steps: SequenceStep[];
  /** Optional caption floated above the top of the line (e.g. a spawn's "Runs alongside…"). */
  caption?: string;
  /** Shown (italic, red) when the line has no steps yet. Pass `''` to show no hint — a switch branch is
   *  complete with just its range; steps are optional there, so it must not nag "add a step". */
  emptyHint?: string;
  /** Insert a step of `type` at gap `slot` (0..length). The parent owns the mutation + any editor-open.
   *  Omit (the locked per-contact view) → no insert "+" slots. */
  onInsert?: (slot: number, type: SequenceStepType) => void;
  /** Remove the step at `i`. Omit (the locked per-contact view) → no remove ✕. */
  onRemoveAt?: (i: number) => void;
  /** Open the content editor for the step at `i` (a message / connect note / …). */
  onEditAt?: (i: number) => void;
  onChangeWaitAt?: (i: number, days: number, unit: WaitUnit) => void;
  onChangeDeadlineAt?: (i: number, days: number) => void;
  /** Open the first insert picker once on mount (a just-inserted container lands ready to add a step). */
  autoOpenFirstPicker?: boolean;
  /** Fires when this line's insert picker opens / closes (so the parent can raise its wrapper). */
  onPickerOpenChange?: (open: boolean) => void;
  /** Locked view: freeze structure — no insert "+", no remove ✕, static durations. Click-to-edit still works. */
  readOnly?: boolean;
}

/**
 * A vertical sub-line of steps that runs OFF the main spine — the shared building block for a spawn's
 * "runs alongside" branch and each of a switch's route branches. Steps are stacked on a vertical
 * connector line with an insert "+" at every gap; each step is a real {@link SequenceGraphNode} (waits
 * in days/weeks, await deadlines, click-to-edit, remove). Owns ONLY its own picker open-state — every
 * mutation is delegated to the parent so the two callers (spawn / switch) keep their own persist logic.
 */
export function SequenceBranchLine({
  steps,
  caption,
  emptyHint = 'add a step',
  onInsert,
  onRemoveAt,
  onEditAt,
  onChangeWaitAt,
  onChangeDeadlineAt,
  autoOpenFirstPicker,
  onPickerOpenChange,
  readOnly = false,
}: Props) {
  const [openSlot, setOpenSlot] = useState<number | null>(null);
  const setOpenSlotOpen = (slot: number | null) => {
    setOpenSlot(slot);
    onPickerOpenChange?.(slot != null);
  };

  // A container just inserted from the "+" menu opens its first insert picker immediately, so you land
  // ready to add the first step instead of on an empty line. One-shot.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenFirstPicker !== true || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    setOpenSlot(0);
    onPickerOpenChange?.(true);
  }, [autoOpenFirstPicker, onPickerOpenChange]);

  useEffect(() => {
    if (openSlot == null) return;
    const close = () => {
      setOpenSlot(null);
      onPickerOpenChange?.(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('[data-sequence-picker], [data-arm-insert]') == null) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [openSlot, onPickerOpenChange]);

  const insertAt = (slot: number, type: SequenceStepType) => {
    setOpenSlotOpen(null);
    onInsert?.(slot, type);
  };

  return (
    <div data-arm-insert className="relative inline-flex flex-col items-center gap-1 px-2">
      {/* The nodes sit on a vertical connector line (like the main spine) — a line that runs alongside, not
          a boxed panel. It starts at the TOP (where the horizontal connector meets it) and runs down; the
          caption floats above that join so the branch reads as one connected line. bg-white nodes occlude
          the line; between them (at the inserts) the line shows. */}
      {caption != null && (
        <span className="absolute -top-3.5 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {caption}
        </span>
      )}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-3 left-1/2 top-0 w-0.5 -translate-x-1/2 bg-lynch-200 dark:bg-slate-700"
      />
      {steps.length === 0 && emptyHint !== '' && <span className="relative z-10 text-[11px] italic text-red-400">{emptyHint}</span>}
      {Array.from({ length: steps.length + 1 }).map((_, slot) => {
        const s = steps[slot];
        return (
          <Fragment key={`slot-${slot}`}>
            {!readOnly && onInsert != null && (
              <div data-arm-insert className="relative z-10">
                <SequenceGraphInsert
                  active={openSlot === slot}
                  inert={openSlot != null && openSlot !== slot}
                  onOpen={() => setOpenSlotOpen(slot)}
                  onPick={(type) => insertAt(slot, type)}
                  onCancel={() => setOpenSlotOpen(null)}
                  disabledReason={(type) => insertDisabledReason(steps, slot, type)}
                />
              </div>
            )}
            {s != null && (
              <div
                className={cn('relative z-10', onEditAt != null && stepIsEditable(s) && 'cursor-pointer')}
                onClick={(e) => {
                  // Don't hijack clicks on the node's own controls (remove ✕, wait input, unit toggle).
                  if (e.target instanceof Element && e.target.closest('button, input, textarea, select, a')) return;
                  if (onEditAt != null && stepIsEditable(s)) onEditAt(slot);
                }}>
                <SequenceGraphNode
                  step={s}
                  dragging={false}
                  invalid={sequenceStepIssue(steps, slot)}
                  onRemove={onRemoveAt != null ? () => onRemoveAt(slot) : undefined}
                  onChangeWait={onChangeWaitAt != null ? (days, unit) => onChangeWaitAt(slot, days, unit) : undefined}
                  onChangeDeadlineDays={onChangeDeadlineAt != null ? (days) => onChangeDeadlineAt(slot, days) : undefined}
                  readOnly={readOnly}
                />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
